import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { IconChevronDown, IconChevronRight, IconX } from '@tabler/icons-react'
import {
  USE_CASE_DATA_KEYS,
  USE_CASE_TOPICS,
  type CreateUseCaseRequest,
  type UseCaseDataKey,
  type UseCaseTopic,
} from '@guilleumes/types'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  parseTagsInput,
  useCaseFormSchema,
  type UseCaseFormValues,
} from '@/lib/schemas/use-case.schema'
import { useCreateUseCase } from '@/hooks/useUseCases'

/** Etiquetes llegibles dels temes. La llista es tanca a M3 amb els casos reals a la mà. */
export const TOPIC_LABEL: Record<UseCaseTopic, string> = {
  messaging: 'Missatgeria',
  billing: 'Facturació',
  calendar: 'Calendari',
  'guest-data': "Dades de l'hoste",
  access: 'Accessos',
  other: 'Altres',
}

export const DATA_KEY_LABEL: Record<UseCaseDataKey, string> = {
  phone: 'Telèfon',
  whatsapp: 'WhatsApp',
  persons: 'Viatgers',
  beds: 'Llits',
  residence: 'Residència',
  company: 'Empresa',
  'arrival-time': "Hora d'arribada",
  invoice: 'Factura',
  other: 'Altres',
}

interface UseCaseFormProps {
  /** Reserves lligades d'entrada. A la fitxa, la del focus. */
  defaultBeds24Ids: string[]
  onCreated?: () => void
  onCancel?: () => void
}

/**
 * Escriure un cas: tres textos i la reserva. Res més és obligatori.
 *
 * La classificació va **plegada** a posta i no perquè sigui secundària, sinó perquè
 * és difícil: «la classificació és important i alhora complicada per a nosaltres»
 * [Pere, 09-SET-2026]. Demanar-la en el moment d'escriure faria que no s'escrivís. La
 * proposa després `scripts/dev/use-cases.mjs --classify`, i qui la valida és una
 * persona des de la llista.
 *
 * Component propi (i no dins de la pàgina) perquè F3.5 el pugui muntar tal qual a la
 * secció "Casos" de la `ReservationSheet` sense duplicar-ne cap validació.
 */
export function UseCaseForm({ defaultBeds24Ids, onCreated, onCancel }: UseCaseFormProps) {
  const [showClassification, setShowClassification] = useState(false)
  const [tagsInput, setTagsInput] = useState('')
  const [extraId, setExtraId] = useState('')

  const createUseCase = useCreateUseCase()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UseCaseFormValues>({
    resolver: zodResolver(useCaseFormSchema),
    defaultValues: {
      whatHappened: '',
      pain: '',
      idea: '',
      beds24Ids: defaultBeds24Ids,
      topic: '',
      dataKey: '',
      questionRef: '',
    },
  })

  const beds24Ids = watch('beds24Ids') ?? []

  const addReservation = () => {
    const candidate = extraId.trim()
    if (!/^\d{8}$/.test(candidate)) {
      toast.error('Un número de reserva de Beds24 són 8 xifres')
      return
    }
    if (beds24Ids.includes(candidate)) {
      setExtraId('')
      return
    }
    setValue('beds24Ids', [...beds24Ids, candidate], { shouldValidate: true })
    setExtraId('')
  }

  const removeReservation = (id: string) => {
    setValue(
      'beds24Ids',
      beds24Ids.filter((value) => value !== id),
      { shouldValidate: true },
    )
  }

  const onSubmit = handleSubmit(async (values) => {
    // El schema ja ha convertit els selects buits en `undefined`: el cos no porta
    // mai un tema buit, i «sense classificar» arriba com a absència, no com a `other`.
    const payload: CreateUseCaseRequest = {
      whatHappened: values.whatHappened,
      pain: values.pain,
      idea: values.idea,
      beds24Ids: values.beds24Ids,
      ...(values.topic ? { topic: values.topic as UseCaseTopic } : {}),
      ...(values.dataKey ? { dataKey: values.dataKey as UseCaseDataKey } : {}),
      ...(values.questionRef ? { questionRef: values.questionRef } : {}),
      ...(parseTagsInput(tagsInput).length > 0 ? { tags: parseTagsInput(tagsInput) } : {}),
    }

    try {
      await createUseCase.mutateAsync(payload)
      toast.success('Cas desat')
      reset({ whatHappened: '', pain: '', idea: '', beds24Ids: defaultBeds24Ids })
      setTagsInput('')
      onCreated?.()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error desconegut'
      toast.error(`No s'ha pogut desar el cas: ${message}`)
    }
  })

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="uc-what">Què ha passat</Label>
        <Textarea id="uc-what" rows={3} placeholder="Què ha passat" {...register('whatHappened')} />
        {errors.whatHappened && (
          <p className="text-xs text-red-600">{errors.whatHappened.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="uc-pain">On fa mal</Label>
        <Textarea id="uc-pain" rows={2} placeholder="On fa mal" {...register('pain')} />
        {errors.pain && <p className="text-xs text-red-600">{errors.pain.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="uc-idea">Què faríem</Label>
        <Textarea id="uc-idea" rows={2} placeholder="Què faríem" {...register('idea')} />
        {errors.idea && <p className="text-xs text-red-600">{errors.idea.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label>Reserves lligades</Label>
        <div className="flex flex-wrap items-center gap-1.5">
          {beds24Ids.map((id) => (
            <Badge key={id} variant="secondary" className="font-mono gap-1">
              {id}
              {beds24Ids.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeReservation(id)}
                  className="hover:text-red-600"
                  aria-label={`Treu ${id}`}
                >
                  <IconX className="w-3 h-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={extraId}
            onChange={(event) => setExtraId(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                addReservation()
              }
            }}
            placeholder="Afegeix una reserva (8 xifres)"
            className="font-mono"
          />
          <Button type="button" variant="outline" onClick={addReservation}>
            Afegeix
          </Button>
        </div>
        {errors.beds24Ids && (
          <p className="text-xs text-red-600">
            {errors.beds24Ids.message ?? 'Revisa els números de reserva'}
          </p>
        )}
      </div>

      <div className="rounded-md border">
        <button
          type="button"
          onClick={() => setShowClassification((open) => !open)}
          className="flex w-full items-center gap-2 p-2 text-sm text-muted-foreground hover:bg-muted/50"
        >
          {showClassification ? (
            <IconChevronDown className="w-4 h-4" />
          ) : (
            <IconChevronRight className="w-4 h-4" />
          )}
          Classificació (opcional — es pot deixar per després)
        </button>

        <div className={cn('space-y-3 border-t p-3', !showClassification && 'hidden')}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="uc-topic">Tema</Label>
              <select
                id="uc-topic"
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                {...register('topic')}
              >
                <option value="">— sense tema —</option>
                {USE_CASE_TOPICS.map((topic) => (
                  <option key={topic} value={topic}>
                    {TOPIC_LABEL[topic]}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="uc-data">Dada</Label>
              <select
                id="uc-data"
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                {...register('dataKey')}
              >
                <option value="">— cap —</option>
                {USE_CASE_DATA_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {DATA_KEY_LABEL[key]}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="uc-question">Pregunta oberta</Label>
              <Input id="uc-question" placeholder="P8" {...register('questionRef')} />
              {errors.questionRef && (
                <p className="text-xs text-red-600">{errors.questionRef.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="uc-tags">Etiquetes</Label>
              <Input
                id="uc-tags"
                value={tagsInput}
                onChange={(event) => setTagsInput(event.target.value)}
                placeholder="expedia, grup"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel·la
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting || createUseCase.isPending}>
          {isSubmitting || createUseCase.isPending ? 'Desant…' : 'Desa el cas'}
        </Button>
      </div>
    </form>
  )
}
